import { type FC, useMemo } from "react";
import ModalContainer from "~/common/components/ModalContainer";
import { useDispatch, useSelector } from "react-redux";
import { restorePreviousSnapshot } from "~/features/configurator/Linear/redux/actions";
import { DefaultButtonIcon, Icon } from "~/common/components/icon/Icon";
import { IconType } from "~/common/components/icon/IconType";
import { Button, Stack } from "~/common/mui";
import { getConfiguratorType, getProductCode } from "~/features/configurator/Linear/redux/selectors";
import { IconSize } from "~/common/components/icon/IconSize";
import { Card } from "~/features/configurator/Linear/components/Card";
import { resolveApiUrl } from "~/utils/url/urlResolver";
import { ConfiguratorType } from "~/domains/configurator/types/ConfiguratorType";
import {
  RESET_URL_CARRIAGES_PREFIX,
  RESET_URL_LINEAR_PREFIX,
  RESET_URL_RAIL_PREFIX,
  RESET_URL_SHAFT_PREFIX,
} from "~/features/configurator/Linear/constants";
import { useTranslation } from "react-i18next";

const ERR_ICON = { type: IconType.Warning, size: IconSize.FONT_20 };
const ARROW_ICON = { type: IconType.ArrowLeft, size: IconSize.FONT_20 };

function getResetPath(configuratorType: ConfiguratorType | undefined) {
  if (configuratorType === ConfiguratorType.Carriage) {
    return RESET_URL_CARRIAGES_PREFIX;
  } else if (configuratorType === ConfiguratorType.Rail) {
    return RESET_URL_RAIL_PREFIX;
  } else if (configuratorType === ConfiguratorType.MetricShaft || configuratorType === ConfiguratorType.InchShaft) {
    return RESET_URL_SHAFT_PREFIX;
  } else {
    return RESET_URL_LINEAR_PREFIX;
  }
}

const InConflictModal: FC = () => {
  const dispatch = useDispatch();
  const configuratorType = useSelector(getConfiguratorType);
  const resetPath = getResetPath(configuratorType);
  const resetUrl =
    resetPath === RESET_URL_SHAFT_PREFIX
      ? resolveApiUrl(`/shaft/${useSelector(getProductCode)}/${resetPath}/reset`)
      : resolveApiUrl(`/${useSelector(getProductCode)}/${resetPath}/reset`);
  const { t } = useTranslation();

  const { onStepBack, onReset } = useMemo(
    () => ({
      onStepBack: () => dispatch(restorePreviousSnapshot()),
      onReset: () => window.location.assign(resetUrl),
    }),
    [],
  );

  return (
    <ModalContainer>
      <div className="scha__fullscreen-loader d-flex flex-justify-center">
        <Card className={"scha__lc__conflict-popup"} disableHover={true}>
          <p className={"mt-0 mb-3 title-medium"}>{t("page.linear.conflict.message")}</p>

          <ul className={"scha__lc__conflict-popup__list"}>
            <li className={"scha__lc__conflict-popup__list__item"}>
              <Icon {...ARROW_ICON} className={"icon-margin f-green"} />
              <span>{t("page.linear.conflict.previous.label")}</span>
            </li>
            <li className={"scha__lc__conflict-popup__list__item"}>
              <Icon {...ERR_ICON} className={"icon-margin f-error-red"} />
              <span>{t("page.linear.conflict.reset.label")}</span>
            </li>
          </ul>
          <div className={"scha__modal__footer"}>
            <Stack className={"d-flex flex-justify-end"} gap={4}>
              <Button
                color={"secondary"}
                startIcon={<DefaultButtonIcon type={ERR_ICON.type} />}
                onClick={onReset}
                data-test-id={"conflict-modal-reset"}
              >
                {t("page.linear.conflict.reset.button")}
              </Button>
              <Button
                startIcon={<DefaultButtonIcon type={ARROW_ICON.type} />}
                onClick={onStepBack}
                data-test-id={"conflict-modal-step-back"}
              >
                {t("page.linear.conflict.previous.button")}
              </Button>
            </Stack>
          </div>
        </Card>
      </div>
    </ModalContainer>
  );
};
export default InConflictModal;
